import { useStyles } from '@team-griffin/stylr';
import { getTitleColor } from 'presentation/utils/colorway';
import { gu2px, useTheme, Colorway, cssTheme } from '@uds/theme-utils';
import { modifier } from '@ux/style-utils';
import { Alignment } from 'domain/constants/alignment';
import cssSides, { Side } from '@team-griffin/css-sides';
import { breakpoints, useBreakpoints } from '@ux/responsive';
import ribbonPadding from 'presentation/utils/ribbonPadding';
import { Layout } from 'domain/constants/layout';
import findResponsiveItem from 'presentation/utils/findResponsiveItem';
import { css } from 'linaria';

import { halfContainerWidth } from './styles';
import { Side as TilesSide } from './constants';

export const getClassNames = () => {
  return {
    title: css`
      padding-right: ${cssTheme.gu3};
    `,
    subtitle: css`
      margin-top: ${cssTheme.gu2};
    `,
    box: css`
      display: flex;
    `,
    wrapperText: css`
      flex: 1;
      flex-basis: auto;
      padding-right: ${cssTheme.gu2};
    `,
    wrapperIcon: css`
      min-width: ${cssTheme.gu3};
      position: relative;
      padding-top: calc(${cssTheme.gu1} / 2);
    `,
  };
};
export default ({
  colorway,
  hover,
  layout,
  leftTile,
  rightTile,
}: {
  colorway: Colorway;
  hover: boolean;
  layout: Layout;
  leftTile: boolean;
  rightTile: boolean;
}) => {
  const { theme } = useTheme();
  const { isMinSm, isMinMd, isMinLg, isMinXl } = useBreakpoints();

  const alignment = findResponsiveItem(useBreakpoints(), [
    layout?.alignmentMobile ?? Alignment.STARTING,
    layout?.alignmentTablet ?? Alignment.STARTING,
    layout?.alignment ?? Alignment.STARTING,
  ]);

  const { tilesSide } = layout;
  const { spacing } = theme;

  return useStyles(
    {
      default: {
        // @ts-ignore: error after upgrading to ux-configs
        root: {
          flex: 1,
          flexBasis: 'auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: isMinLg ? 'center' : 'flex-start',
          textAlign: 'left',
          textDecoration: 'none',
          height: '100%',
          ...ribbonPadding({
            spacing,
            isMinSm,
            isMinLg,
            layout: {
              shrinkLeading: isMinLg && rightTile,
              shrinkTrailing: isMinLg && leftTile,
            },
          }),
        },
        container: {
          ...cssSides('padding', Side.X, gu2px(1, spacing)),
          ...cssSides('margin', Side.X, 'auto'),
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
        },
        icon: {
          color: getTitleColor(theme, colorway),
          opacity: 1,
          width: gu2px(3, spacing),
          position: 'absolute',
          right: 0,
        },
      },
      hover: {
        root: {
          textDecoration: 'none',
        },
        icon: {
          opacity: 1,
          marginRight: 0,
          animation: 'arrowAnimation 0.3s',
          animationTimingFunction: 'ease-in-out',
          animationDirection: 'normal',
          animationFillMode: 'forwards',
        },
      },

      [modifier('alignment', Alignment.CENTER)]: {
        root: {
          textAlign: 'center',
        },
      },

      isMinSm: {
        container: {
          maxWidth: breakpoints.sm - gu2px(1, spacing),
        },
      },

      isMinMd: {
        container: {
          // theu're side by side so need an additional 1GU removing
          maxWidth: halfContainerWidth(breakpoints.md, spacing),
        },
      },

      isMinLg: {
        container: {
          maxWidth: halfContainerWidth(breakpoints.lg, spacing),
        },
      },
      isMinXl: {
        container: {
          maxWidth: halfContainerWidth(breakpoints.xl, spacing),
        },
      },
      leftTileMedium: {
        container: {
          marginRight: 0,
        },
      },
      rightTileMedium: {
        container: {
          marginLeft: 0,
        },
      },
      leftTileLarge: {
        container: {
          marginRight: 'auto',
          marginLeft: 0,
        },
      },

      tilesStartLarge: {
        container: {
          marginLeft: 'auto',
          marginRight: 0,
        },
      },
    },
    [
      { hover },
      { alignment },
      { isMinSm },
      { isMinMd },
      { isMinLg },
      { isMinXl },
      { leftTileMedium: leftTile && isMinMd },
      { rightTileMedium: rightTile && isMinMd },
      { leftTileLarge: leftTile && isMinLg },
      { tilesStartLarge: isMinLg && tilesSide === TilesSide.START },
    ],
  );
};
